import { Component, OnInit } from '@angular/core';
import { StripeService } from '../../../services/stripe.service';
import { UserService } from '../../../services/user.service';
import { environment } from '../../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { ReportDateService } from 'src/app/services/report-date.service';
import { UtilityService } from 'src/app/services/utility.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-billing-portal',
  templateUrl: './billing-portal.component.html',
  styleUrls: ['./billing-portal.component.scss']
})
export class BillingPortalComponent implements OnInit {
  loading = true;
  error = false;
  subscriptionMissing = false;
  billingStatus: any = {};
  studioId: string = '';
  stripeCustomerPortalUrl: string = '';
  customerId: string;
  subscription: any = null;
  subscriptionData: any = null;
  paymentHistory: any[] = [];
  paymentMethod: any = null;

  constructor(
    private stripeService: StripeService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private authService: AuthService,
    private utilityService: UtilityService,
    private reportDateService: ReportDateService
  ) { }

  ngOnInit(): void {
    // Check for session_id parameter for payment success
    this.route.queryParams.subscribe(params => {
      if (params['session_id']) {
        this.checkPaymentSuccess(params['session_id']);
      }

      if (params['studio_id']) {
        this.studioId = params['studio_id'];
      } else {
        // Get current studio ID from auth service
        this.studioId = this.authService.getCurrentStudioId() || '1'; // Default to '1' if not available

        if (!this.studioId) {
          this.error = true;
          this.loading = false;
          this.showError('No studio selected. Please select a studio first.');
          return;
        }
      }

      this.loadSubscriptionData();
    });
  }

  async checkPaymentSuccess(sessionId: string) {
    try {
      this.stripeService.getSession(sessionId)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            LoggerService.error('Error checking payment status:', error);
            return of(null);
          })
        )
        .subscribe(
          data => {
            if (data?.status === 'complete') {
              this.showSuccess('Payment processed successfully');
            }
          }
        );
    } catch (error) {
      LoggerService.error('Error checking payment status:', error);
    }
  }

  loadSubscriptionData(): void {
    this.loading = true;
    this.error = false;

    LoggerService.log('Loading subscription data for studio ID:', this.studioId);

    // Get customer ID associated with studio
    this.stripeService.getStudioCustomer(this.studioId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          LoggerService.error('Error fetching customer data:', error);
          LoggerService.log('API Response:', error.error);

          if (error.status === 404) {
            this.subscriptionMissing = true;
          } else {
            this.error = true;
          }
          this.loading = false;
          return of(null);
        })
      )
      .subscribe(
        data => {
          LoggerService.log('Studio customer data response:', data);

          if (data && (data.stripeCustomerId || data.status === 'active')) {
            this.customerId = data.stripeCustomerId;
            LoggerService.log('Found customer ID:', this.customerId);

            this.subscriptionData = data; // Store the response
            this.formatSubscriptionData(data); // Format and display it
            this.fetchPaymentHistory();
            this.loading = false;
          } else {
            LoggerService.log('No valid subscription data found');
            this.subscriptionMissing = true;
            this.loading = false;
          }
        }
      );
  }

  fetchSubscriptionDetails(): void {
    // Since we're using subscription-status endpoint now, we already have the data
    // Just need to format it for the UI
    if (!this.customerId) {
      LoggerService.error('No customer ID available');
      this.error = true;
      this.loading = false;
      return;
    }

    // Check if we already have subscription data from getStudioCustomer call
    if (this.subscriptionData) {
      this.formatSubscriptionData(this.subscriptionData);
      this.loading = false;
      return;
    }

    // Otherwise, fetch it
    this.stripeService.getCustomerSubscription(this.studioId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          LoggerService.error('Error fetching subscription details:', error);
          this.error = true;
          this.loading = false;
          return of(null);
        })
      )
      .subscribe(
        data => {
          if (data) {
            this.formatSubscriptionData(data);
          }
          this.loading = false;
        }
      );
  }

  formatSubscriptionData(data: any): void {
    // Store the raw subscription data
    this.subscriptionData = data;

    // Format for billing status object
    this.billingStatus = {
      autoPayEnabled: data.autoPayEnabled || false,
      autoPayDate: data.autoPayDate || '15th',
      subscriptionStatus: data.status || 'inactive',
      nextPaymentDate: data.nextBillingDate ? this.formatDate(new Date(data.nextBillingDate).getTime() / 1000) : 'N/A',
      lastPaymentDate: data.lastPaymentDate ? this.formatDate(new Date(data.lastPaymentDate).getTime() / 1000) : 'N/A',
      paymentStatus: data.status === 'active' ? 'current' : 'overdue',
      accountName: data.studioName || this.authService.selectedStudio?.name || 'Your Studio',
      stripeCustomerId: data.stripeCustomerId || this.customerId,
      stripeSubscriptionId: data.stripeSubscriptionId || ''
    };

    // Extract payment method if available (may need to adjust based on actual API response)
    if (data.paymentMethod) {
      this.paymentMethod = data.paymentMethod;
    }
  }

  fetchPaymentHistory(): void {
    this.stripeService.getPaymentHistory(this.studioId) // Use studioId instead of customerId
      .pipe(
        catchError((error: HttpErrorResponse) => {
          LoggerService.error('Error fetching payment history:', error);
          return of({ payments: [] });
        })
      )
      .subscribe(
        data => {
          this.paymentHistory = data.payments || [];

          // Update billing status payment history
          this.billingStatus.paymentHistory = this.paymentHistory.map(payment => ({
            date: this.formatDate(new Date(payment.date).getTime() / 1000),
            amount: this.formatCurrency(payment.amount),
            status: payment.status.toLowerCase(),
            transactionId: payment.transactionId || payment.id
          }));
        }
      );
  }

  openCustomerPortal(): void {
    if (!this.studioId) {
      this.showError('No studio selected. Please contact support.');
      LoggerService.error('No studio ID available for creating portal session');
      return;
    }

    this.stripeService.createCustomerPortalSession(this.studioId)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          LoggerService.error('Error creating portal session:', error);

          // If we get a 404, it means the customer ID might not be valid in Stripe
          if (error.status === 404 && this.studioId) {
            this.showError('Your subscription information needs to be updated. Please contact support.');
          } else {
            this.showError('Failed to access billing portal: ' + (error.error?.message || 'Unknown error'));
          }

          return of(null);
        })
      )
      .subscribe(
        response => {
          // Redirect to Stripe hosted portal
          if (response && response.url) {
            window.location.href = response.url;
          } else {
            this.showError('Failed to access billing portal: Invalid response from server');
          }
        }
      );
  }

  async updateAutoPay(event: any) {
    const isEnabled = event.checked;

    try {
      this.stripeService.updateSubscriptionCollectionMethod(
        this.billingStatus.stripeSubscriptionId,
        isEnabled ? 'charge_automatically' : 'send_invoice'
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          LoggerService.error('Error updating auto-pay setting:', error);
          this.showError('Failed to update auto-pay settings');
          // Reset toggle to previous state
          event.source.checked = !isEnabled;
          return of(null);
        })
      )
      .subscribe(
        response => {
          if (response) {
            this.billingStatus.autoPayEnabled = isEnabled;
            this.showSuccess(`Auto-pay has been ${isEnabled ? 'enabled' : 'disabled'}`);
          }
        }
      );
    } catch (error) {
      LoggerService.error('Error updating auto-pay:', error);
      this.showError('Failed to update auto-pay settings');
      // Reset toggle to previous state
      event.source.checked = !isEnabled;
    }
  }

  formatDate(timestamp: number): string {
    if (!timestamp) return 'N/A';
    return this.reportDateService.formatDate(timestamp * 1000, 'N/A');
  }

  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined) return 'N/A';
    return this.utilityService.formatCurrency(amount / 100, {
      includeSymbol: true,
      useGrouping: true
    });
  }

  getPaymentMethodIcon(type: string): string {
    if (type === 'card') {
      return 'credit_card';
    } else if (type === 'ach_debit') {
      return 'account_balance';
    }
    return 'payment';
  }

  getFormattedPaymentMethod(method: any): string {
    if (!method) return 'No payment method on file';

    if (method.type === 'card') {
      return `${this.capitalize(method.card.brand)} ending in ${method.card.last4}`;
    } else if (method.type === 'ach_debit') {
      return `Bank account (${method.ach_debit.bank_name}) ending in ${method.ach_debit.last4}`;
    }

    return 'Unknown payment method';
  }

  capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  showSuccess(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['success-snackbar']
    });
  }

  showError(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  showInfo(message: string) {
    this.snackBar.open(message, 'Close', {
      duration: 5000
    });
  }
}
